import { useNextSanityImage } from 'next-sanity-image';
import sanitizeHtml from 'sanitize-html';

import { getClient } from '@/lib/sanity';

import type { SanityBlogAuthorType, SanityBlogPostType } from '@/types/sanity';

interface BlogSchemaProps {
  url: string;
  datePublished: string;
  headline: string;
  articleBody: string;
  description: string;
  image: SanityBlogPostType['featuredImage'];
  author: SanityBlogAuthorType;
  locale: string;
}

const client = getClient();

const BlogSchema = ({
  datePublished,
  headline,
  articleBody,
  description,
  image,
  author,
  locale,
  url,
}: BlogSchemaProps) => {
  const imageProps = useNextSanityImage(client, image, {
    imageBuilder: (imageUrlBuilder, options) => {
      return imageUrlBuilder
        .width(
          options.width ||
            Math.min(options.originalImageDimensions.width, 1920),
        )
        .auto('format')
        .fit('clip');
    },
  });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          url,
          datePublished,
          headline,
          articleBody: sanitizeHtml(articleBody),
          description,
          image: imageProps?.src
            ? {
                '@type': 'ImageObject',
                url: imageProps.src,
                width: imageProps.width,
                height: imageProps.height,
              }
            : {},
          author: author?.name
            ? {
                '@type': 'Person',
                name: author.name,
                url: `https://www.multiverse.io/${locale}/blog/author/${author.slug.current}`,
              }
            : {},
        }),
      }}
    />
  );
};

export { BlogSchema };
